import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ServiceDto } from '@impact/data';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { arrowRightIcon } from '../../../../icons';
import { ToastService } from '../../../core/toast.service';
import { WorkshopService } from '../../../core/workshop.service';
import { IBooking } from '../../../models/book-workshop.models';

interface ICheckableService extends ServiceDto {
    checked?: boolean;
}

@Component({
    selector: 'app-book-workshop-select-service',
    template: `
        <section class="flow-step__header">
            <h1 class="h4">{{ 'book_workshop.select_service' | translate }}</h1>
            <div>{{ 'book_workshop.select_services_to_book' | translate }}</div>
        </section>

        <div class="loader" *ngIf="loading"></div>

        <ng-container *ngIf="services$ | async as services">
            <ul class="book-workshop-select-service-list">
                <li *ngFor="let service of services" class="book-workshop-select-service-item">
                    <input
                        class="book-workshop-select-service-item__checkbox"
                        type="checkbox"
                        [id]="'service__' + service.code"
                        [checked]="service.checked"
                        (change)="onSelectionChange(service)"
                    />
                    <label
                        [for]="'service__' + service.code"
                        class="book-workshop-select-service-item__content"
                        appCircleCheckmark
                        [condition]="service.checked"
                    >
                        <div class="book-workshop-select-service-item__title">
                            {{ service.title }}
                        </div>
                        <div class="book-workshop-select-service-item__description">
                            {{ service.description }}
                        </div>
                        <div *ngIf="service.price as price" class="book-workshop-select-service-item__price">
                            <ng-container *ngIf="price >= 1; else lessThan1">
                                {{ price | defaultcurrency }}
                            </ng-container>
                            <ng-template #lessThan1>
                                <ng-container *ngIf="price === 0; else variablePrice">
                                    {{ 'generic.for_free' | translate }}
                                </ng-container>
                                <ng-template #variablePrice>
                                    {{ 'generic.variable_price' | translate }}
                                </ng-template>
                            </ng-template>
                        </div>
                    </label>
                </li>
            </ul>

            <div class="book-workshop-select-service__comment-wrapper">
                <div class="book-workshop-summary__heading">{{ 'book_workshop.comment_header_text' | translate }}</div>
                <textarea
                    class="form-text-area"
                    [(ngModel)]="userComment"
                    [placeholder]="'book_workshop.comment_header_text' | translate"
                    rows="5">
                </textarea>
            </div>
        </ng-container>

        <div class="book-workshop-flow__bottom">
            <div class="book-workshop-flow__bottom-content">
                <app-button
                    buttonClass="book-workshop-stepper-content__continue-button button--tertiary"
                    (click)="previousStep.emit()"
                    [title]="'book_workshop.back' | translate"
                ></app-button>
                <app-button
                    buttonClass="book-workshop-stepper-content__continue-button button--primary"
                    [disabled]="(checkedServices$ | async)?.length === 0 || loading"
                    (handleClick)="onNextStepClick()"
                    [title]="'book_workshop.continue' | translate">
                    <i class="button__icon">${arrowRightIcon}</i>
                </app-button>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookWorkshopSelectServiceComponent implements OnInit {
    @Input() booking: IBooking | null;
    @Output() nextStep = new EventEmitter();
    @Output() previousStep = new EventEmitter();

    services$: Observable<ICheckableService[]>;
    checkedServices$ = new BehaviorSubject<ICheckableService[]>([]);

    userComment: string;
    loading = true;

    constructor(
        private workshopService: WorkshopService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef) {}

    ngOnInit(): void {
        if (this.booking) {
            this.userComment = this.booking?.comment ?? ''

            this.services$ = combineLatest([
                this.workshopService
                    .getShopServices(this.booking)
                    .pipe(
                        tap(() => {
                            this.loading = false;
                            this.cdr.markForCheck();
                        }),
                        catchError((error: HttpErrorResponse) => {
                            this.toastService.show(this.translateService.instant('book_workshop.error_message____generic'));
                            this.loading = false;
                            this.cdr.markForCheck();
                            return throwError(error);
                        })
                    ),
                this.checkedServices$])
                .pipe(
                    map(([shopServices, checkedServices]) =>
                        shopServices.map((shopService) => ({
                            ...shopService,
                            checked: !!shopService.code && checkedServices.findIndex((service) => service.code === shopService.code) !== -1,
                        }))
                    )
                );

            this.checkedServices$.next(this.booking.services || []);
        }
    }

    onNextStepClick() {
        this.nextStep.emit({
            services: this.checkedServices$.value,
            comment: this.userComment
        });
    }

    onSelectionChange(service?: ICheckableService) {
        if (!service) {
            return;
        }

        const checkedServices = this.checkedServices$.value;
        const serviceCodeIndex = checkedServices.findIndex((checkedService) => checkedService.code === service.code);

        if (serviceCodeIndex === -1) {
            checkedServices.push(service);
        } else {
            checkedServices.splice(serviceCodeIndex, 1);
        }

        this.checkedServices$.next(checkedServices);
    }
}
